import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, TrendingDown, Wallet, Landmark, Coins } from "lucide-react";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Cash() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/cash-summary").then(r => setS(r.data));
  }, []);

  if (!s) return <div className="p-8 font-mono text-sm text-zinc-500">[ loading... ]</div>;

  const cards = [
    { label: "Total Billed", value: s.total_billed, icon: TrendingUp, accent: "" },
    { label: "Collected", value: s.total_collected, icon: TrendingUp, accent: "text-[#039855]" },
    { label: "Outstanding", value: s.outstanding, icon: TrendingDown, accent: "text-[#D92D20]" },
    { label: "Expenses", value: s.total_expenses, icon: TrendingDown, accent: "text-[#D92D20]" },
  ];

  const netCards = [
    { label: "Net Profit", value: s.net_profit, icon: Coins, accent: s.net_profit >= 0 ? "text-[#039855]" : "text-[#D92D20]" },
    { label: "Cash in Hand", value: s.cash_in_hand, icon: Wallet, accent: "text-[#002FA7]" },
    { label: "Bank Balance", value: s.bank_balance, icon: Landmark, accent: "text-[#002FA7]" },
  ];

  const cats = Object.entries(s.expenses_by_category || {}).sort((a, b) => b[1] - a[1]);
  const maxCat = Math.max(1, ...cats.map(([, v]) => v));

  return (
    <div className="p-6 md:p-8 max-w-[1400px]">
      <div className="mb-8">
        <p className="label-caps mb-2">Books</p>
        <h1 className="font-display font-black text-4xl tracking-tighter">Cash & Money Summary</h1>
        <p className="text-zinc-600 mt-2">Practice finances at a glance.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white border border-zinc-200 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className="text-zinc-500" />
                <p className="label-caps">{c.label}</p>
              </div>
              <div className={`font-display font-black text-2xl md:text-3xl tracking-tighter ${c.accent}`}>{fmt(c.value)}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {netCards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-zinc-900 text-white p-6">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} className="text-zinc-400" />
                <p className="label-caps text-zinc-400">{c.label}</p>
              </div>
              <div className={`font-display font-black text-4xl tracking-tighter ${c.accent || "text-white"}`}>{fmt(c.value)}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-zinc-200 p-6">
        <h3 className="font-display font-bold text-lg tracking-tight mb-4">Expenses by Category</h3>
        {cats.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono">[ no expenses recorded yet ]</p>
        ) : (
          <div className="space-y-3">
            {cats.map(([k, v]) => (
              <div key={k}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm font-medium">{k}</span>
                  <span className="text-sm font-mono">{fmt(v)}</span>
                </div>
                <div className="h-2 bg-zinc-100">
                  <div className="h-full bg-zinc-900" style={{ width: `${(v / maxCat) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
